import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation, DgtNavigationItem } from 'app/core/navigation/navigation.types';
import { map, Observable, ReplaySubject, tap } from 'rxjs';
import { UserService } from '../user/user.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    private _userService = inject(UserService);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            map((navigation) => this._buildNavigationBasedOnPermissions(navigation)),
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );
    }

    private _filterNavigationItems(navigationItems: DgtNavigationItem[], userPermissions: string[]): DgtNavigationItem[] {
        return navigationItems.filter(item => {
            const itemPermissions = item.permitions || [];
            
            if (itemPermissions.length === 0) {
                if (item.children) {
                    item.children = this._filterNavigationItems(item.children, userPermissions);
                }
                return true;
            }

            const hasAccess = itemPermissions.some(permission => 
                userPermissions.includes(permission)
            );
    
            if (item.children) {
                item.children = this._filterNavigationItems(item.children, userPermissions);
            }
    
            return hasAccess;
        });
    }

    private _buildNavigationBasedOnPermissions(navigation: Navigation): Navigation {
        const user = this._userService.user;
        const userPermissions = user?.permissions || [];        
    
        navigation.compact = this._filterNavigationItems(navigation.compact, userPermissions);
        navigation.default = this._filterNavigationItems(navigation.default, userPermissions);
    
        return navigation;
    }
}
